"use client"

import { useRef, type ReactNode } from "react"

import { Button } from "@/components/ui/button"
import { Spinner } from "@/components/ui/spinner"
import {
  useGetCandidatesCandidateId,
  useGetJobsJobIdCandidate,
} from "openapi/api/endpoints"
import type { CandidateProfile } from "openapi/api/model"
import type { CommentOverlayMode, OverlayTab } from "./candidate-profile-sheet"
import { OverlaySheet } from "@/components/graphite/overlay-sheet"
import { GraphiteCandidateOverlay } from "./candidates/graphite-candidate-overlay"

type CandidateProfileEnvelope = { candidate: CandidateProfile | null }

type CandidateProfileOverlayContainerProps = {
  candidateId?: string | null
  /**
   * The job the record was opened *from*. Its presence switches the profile
   * onto the job-scoped endpoint, so only pass it when there really is a job
   * context — a job's pipeline or overview.
   */
  jobId?: string | null
  /**
   * The job outreach copy is written against, when there is no job context.
   * `GET /outreach` needs a job and the candidates list has none, so the caller
   * resolves one. Deliberately separate from `jobId`: it must not change which
   * endpoint loads the record, or a candidate whose only pipeline link points
   * at a stale job would fail to open at all.
   */
  outreachJobId?: string | null
  /**
   * That job's title, when the caller knows it. The job-scoped profile carries
   * its own; this is for the candidates list, which resolves the job from the
   * pipeline link rather than fetching the record job-scoped.
   */
  outreachJobTitle?: string
  /**
   * The stage that job–candidate link is at. The base candidate endpoint does
   * not carry a stage, so without this the record opened from the candidates
   * list would show a stage ladder with nothing marked current — and the stage
   * the candidate is already in would be offered as a move.
   */
  currentStage?: string | null
  activeTab?: OverlayTab
  onTabChange?: (tab: OverlayTab) => void
  activeCommentMode?: CommentOverlayMode
  activeCommentId?: string | null
  onCommentChange?: (next: {
    mode: CommentOverlayMode
    commentId?: string | null
  }) => void
  onClose: () => void
}

/**
 * URL-owned candidate profile loader. Callers own route/search state; this
 * container owns candidateId -> profile hydration so direct links and list
 * clicks share one overlay contract.
 */
export function CandidateProfileOverlayContainer({
  candidateId,
  jobId,
  outreachJobId,
  outreachJobTitle,
  currentStage,
  activeTab,
  onTabChange,
  activeCommentMode,
  activeCommentId,
  onCommentChange,
  onClose,
}: CandidateProfileOverlayContainerProps) {
  const hasCandidate = Boolean(candidateId)
  const isJobScoped = Boolean(candidateId && jobId)

  const candidateQuery = useGetCandidatesCandidateId(candidateId ?? "", {
    query: { enabled: hasCandidate && !isJobScoped },
  })

  const jobCandidateQuery = useGetJobsJobIdCandidate(
    jobId ?? "",
    { candidate_id: candidateId ?? "" },
    { query: { enabled: isJobScoped } },
  )

  // The base candidate endpoint returns { candidate }, while the job-scoped
  // candidate endpoint returns the profile directly. The sheet should not need
  // to care which URL opened it.
  const candidateProfile = isJobScoped
    ? jobCandidateQuery.data
    : readCandidateProfile(candidateQuery.data)
  const isLoading = isJobScoped
    ? jobCandidateQuery.isLoading
    : candidateQuery.isLoading
  const isFetching = isJobScoped
    ? jobCandidateQuery.isFetching
    : candidateQuery.isFetching
  const isError = isJobScoped ? jobCandidateQuery.isError : candidateQuery.isError
  const error = isJobScoped ? jobCandidateQuery.error : candidateQuery.error

  /*
    Closing clears the id, which disables the query, which empties the profile —
    while the sheet is still sliding out. Without holding the last profile the
    record would swap to "Candidate unavailable" on its way off screen.
  */
  const lastProfile = useRef<CandidateProfile | null>(null)
  if (candidateProfile) lastProfile.current = candidateProfile as CandidateProfile
  const exitingProfile = !hasCandidate ? lastProfile.current : null

  /*
    One sheet, always rendered. Loading, error and the record are its children,
    so the panel mounts once and slides once — the three used to be sibling
    branches that each built their own frame, which made the record appear,
    disappear and reappear as the fetch resolved.
  */
  return (
    <OverlaySheet
      open={hasCandidate}
      onClose={onClose}
      label="Candidate record"
    >
      {exitingProfile ? (
        <GraphiteCandidateOverlay
          candidateProfile={exitingProfile}
          jobId={jobId ?? outreachJobId ?? null}
          jobTitle={outreachJobTitle}
          currentTab={activeTab ?? "none"}
          onTabChange={() => {}}
          onClose={onClose}
        />
      ) : isLoading || (isFetching && !candidateProfile) ? (
        <OverlayStatus>
          <Spinner className="h-5 w-5" />
          <span className="text-[12.5px]">Opening candidate profile…</span>
        </OverlayStatus>
      ) : isError || !candidateProfile ? (
        <OverlayStatus>
          <p className="text-[13px] font-medium text-foreground">
            Candidate unavailable
          </p>
          <p className="max-w-[320px] text-[12.5px]">
            {describeProfileError(error, isJobScoped)}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 text-xs"
          >
            Close
          </Button>
        </OverlayStatus>
      ) : (
        <GraphiteCandidateOverlay
          /*
            Keyed by the record, so switching candidates inside the open sheet
            builds a new one rather than handing the next person the last one's
            state: which of the task screens was open, how far a windowed list
            had been expanded, a half-written comment. The sheet itself stays
            mounted around it, so the slide is untouched.
          */
          key={candidateId ?? "none"}
          candidateProfile={candidateProfile}
          jobId={jobId ?? outreachJobId ?? null}
          jobTitle={outreachJobTitle}
          currentStage={currentStage}
          currentTab={activeTab ?? "none"}
          onTabChange={(tab) => onTabChange?.(tab)}
          onClose={onClose}
          activeCommentMode={activeCommentMode}
          activeCommentId={activeCommentId}
          onCommentChange={onCommentChange}
        />
      )}
    </OverlaySheet>
  )
}

/**
 * Says what actually went wrong. "Unavailable in the current context" told the
 * user nothing and hid the difference between a candidate that is not on a job,
 * one that is gone, and the API being unreachable.
 */
function describeProfileError(error: unknown, isJobScoped: boolean) {
  const status = (error as { response?: { status?: number } } | null)?.response
    ?.status

  if (status === 404) {
    return isJobScoped
      ? "This candidate is not on that job's pipeline, or the job no longer exists."
      : "This candidate no longer exists."
  }
  if (status === 403) return "You do not have access to this candidate."
  if (status && status >= 500) {
    return "The server could not return this candidate. Try again in a moment."
  }
  if (error) return "The candidate could not be loaded. Check your connection."
  return "No record came back for this candidate."
}

function OverlayStatus({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full flex-col items-center justify-center gap-3 bg-background p-6 text-center text-muted-foreground">
      {children}
    </div>
  )
}

function readCandidateProfile(
  response: CandidateProfile | CandidateProfileEnvelope | undefined,
): CandidateProfile | null {
  if (!response) return null
  if ("candidate" in response) return response.candidate ?? null
  return response
}



